import { Icon, IconButton, Paragraph, Tooltip } from "@smartsuite/react-ui/lib";
import cx from "classnames";
import React, { useRef } from "react";
import { $t } from "../../utils/intl";
import { formFieldControlText } from "./FormFieldControl.text";
import { InputGroupState } from "@smartsuite/react-ui/lib/components/forms/InputGroup/InputGroup.constants";
import { GrayColors } from "@smartsuite/types";

export interface FormFieldControlProps {
  label?: string;
  state?: InputGroupState;
  caption?: string;
  captionAsTooltip?: boolean;
  required: boolean;
  readOnly?: boolean;
  htmlFor?: string;
  errorMessage?: string;
  children: React.ReactNode;
}

export const FormFieldControl: React.FC<FormFieldControlProps> = ({
  label,
  state,
  captionAsTooltip,
  caption,
  htmlFor,
  required,
  readOnly,
  errorMessage,
  children,
}) => {
  const labelContainerRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className={cx("form-field-control", {
        "form-field-control--read-only": readOnly,
        [`form-field-control--state-${state}`]: state,
      })}
      aria-label="form-field-control"
    >
      <label htmlFor={htmlFor} className="form-field-control__label">
        <div ref={labelContainerRef} className="form-field-control__label-container">
          {label && (
            <Paragraph weight="medium" size="l-semi-plus" color="currentColor">
              {label}
            </Paragraph>
          )}

          {caption && captionAsTooltip && (
            <div
              className={cx("form-field-control__tooltip", {
                "form-field-control__tooltip--has-label": label,
              })}
            >
              <Tooltip
                text={caption}
                placements={["top"]}
                padding={2}
                target={<IconButton name="info-baloon" size="XS" ariaLabel={label ?? ""} />}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error: strict mode enabled
                triggerOpenElement={labelContainerRef}
              />
            </div>
          )}
        </div>

        {required && (
          <Paragraph
            className="form-field-control__required"
            color="error"
            size="xxxs"
            weight="semibold"
          >
            {$t(formFieldControlText.required)}
          </Paragraph>
        )}

        {readOnly && (
          <Icon
            className="form-field-control__lock"
            ariaLabel="lock"
            name="lock"
            color={GrayColors["G-4"]}
            size="S"
          />
        )}
      </label>

      {caption && !captionAsTooltip && (
        <div className="form-field-control__caption">
          <Paragraph size="s" color="secondary">
            {caption}
          </Paragraph>
        </div>
      )}

      <div className="form-field-control__body">{children}</div>

      {errorMessage && (
        <div className="form-field-control__error-message">
          <Paragraph color="error" size="s">
            {errorMessage}
          </Paragraph>
        </div>
      )}
    </div>
  );
};
